/**
 * MEASUREMENT PRESENTATION — one implementation, every consumer.
 *
 * OWNER: app-architect (the package); promoted out of
 * `apps/web/src/components/record.tsx` after the D-025 conformance harness
 * surfaced all three as forks. Web and the native harness now render the six
 * group headings, the left/right pairs and the inch display from THIS module,
 * which is the last crop-the-chrome difference closed.
 *
 * WHAT IS HERE, and why each belongs in `@pt/view` rather than in a screen:
 *
 *   · `MEASUREMENT_GROUP_COPY` — a MAPPING from the schema's group id to a
 *     catalogue key. The WORDS stay in `design/copy*.json`, where the UX
 *     Writer owns them; what moves is the correspondence between
 *     `rules.json → measurementSchema.groups[].id` and the key that names it.
 *     Two surfaces disagreeing about which heading a field sits under is a
 *     different record on each platform, which `RULING-006` iii forbids.
 *
 *   · `MEASUREMENT_PAIRS` — the left/right measures. `[T-27]` and
 *     `ST-CUTSHEET-ASYMMETRY` require both sides SIDE BY SIDE, always rendered
 *     even when equal, and NEVER averaged. That is a domain-derived
 *     presentation rule, and a platform that forgot one pair would silently
 *     average a body.
 *
 *   · `toInches` / `inchesToCm` — display converts to the nearest ⅛ inch, and
 *     an inch ENTRY is converted once, on input, at the schema's 0.1 cm step.
 *     STORAGE NEVER ROUND-TRIPS (`DOMAIN_RULES` §1.3). Two implementations of a
 *     rounding rule is two different numbers under the same label.
 *
 * PLATFORM-FREE. No DOM, no `node:*`, no React, no `react-native`. The only
 * import is a TYPE from `@pt/copy`, which is erased at compile time — this
 * module holds no string and resolves no key.
 */

import type { CopyKey } from '@pt/copy';

/* ------------------------------------------------------------- the groups */

/**
 * `rules.json → measurementSchema.groups[].id` → the catalogue key that names
 * it. Groups A–F of `DOMAIN_RULES` §1.4, in the tailor's order, never
 * alphabetical.
 *
 * `notes` is deliberately absent: the tailor's notes are not a measurement
 * group and are headed by `meas.notes.heading`, which carries the author's
 * name as a slot.
 */
export const MEASUREMENT_GROUP_COPY: Readonly<Record<string, CopyKey>> = {
  body_core: 'meas.group.a',
  torso: 'meas.group.b',
  arms: 'meas.group.c',
  lower: 'meas.group.d',
  waistcoat: 'meas.group.e',
  posture: 'meas.group.f',
};

/** The catalogue key for a schema group, or `undefined` for `notes`. */
export function measurementGroupCopyKey(groupId: string): CopyKey | undefined {
  return MEASUREMENT_GROUP_COPY[groupId];
}

/* -------------------------------------------------------------- the pairs */

/**
 * A measure taken on both sides. Left and right are held apart and shown
 * together; the two are NEVER averaged into one figure ([T-27]).
 */
export interface MeasurementPair {
  readonly leftId: string;
  readonly rightId: string;
  /**
   * The field whose label names the ROW. It is the left field: "Half back,
   * left" heads a row whose two columns are then labelled left and right, so
   * the row reads as one measure rather than two unrelated numbers.
   */
  readonly labelFieldId: string;
}

export const MEASUREMENT_PAIRS: readonly MeasurementPair[] = [
  { leftId: 'half_back_left', rightId: 'half_back_right', labelFieldId: 'half_back_left' },
  {
    leftId: 'sleeve_length_left',
    rightId: 'sleeve_length_right',
    labelFieldId: 'sleeve_length_left',
  },
  {
    leftId: 'shoulder_slope_left_deg',
    rightId: 'shoulder_slope_right_deg',
    labelFieldId: 'shoulder_slope_left_deg',
  },
  /* rules 1.5.0, PLATFORM-1 finding 3. The legs were single and the sleeves
     were paired — the record was measuring as if bodies were symmetrical below
     the waist. These are the pairs, and the test above is what made adding them
     here compulsory rather than optional: "a platform that forgot one pair
     would silently average a body." */
  { leftId: 'outseam_left', rightId: 'outseam_right', labelFieldId: 'outseam_left' },
  { leftId: 'inseam_left', rightId: 'inseam_right', labelFieldId: 'inseam_left' },
];

/** The pair a field belongs to, if it belongs to one. */
export function pairContaining(fieldId: string): MeasurementPair | undefined {
  return MEASUREMENT_PAIRS.find((pair) => pair.leftId === fieldId || pair.rightId === fieldId);
}

/* --------------------------------------------------------------- the unit */

/** The single conversion constant. Nothing else in the product divides by this. */
export const CM_PER_INCH = 2.54;

/**
 * Inches to centimetres, at the schema's own 0.1 cm step.
 *
 * The AUTHORING direction, and the reason it is here rather than in the form:
 * an inch entry is converted ONCE, on input, and the tailor sees the converted
 * centimetre value before he locks (`DOMAIN_RULES` §1.3). Two implementations
 * of that conversion is two different records for the same body.
 */
export function inchesToCm(inches: number): number {
  return Number((inches * CM_PER_INCH).toFixed(1));
}

/**
 * Centimetres to the nearest ⅛ inch, as the tailor's own notation: `40`,
 * `40 1/2`, `40 3/8`.
 *
 * DISPLAY ONLY. The record stores centimetres and storage never round-trips
 * through inches (`DOMAIN_RULES` §1.3) — a value shown as `40 1/2` and read
 * back as `102.9` would be a measurement the tailor never took.
 */
export function toInches(cm: number): string {
  const eighths = Math.round((cm / CM_PER_INCH) * 8);
  const whole = Math.floor(eighths / 8);
  const rest = eighths % 8;
  if (rest === 0) return `${whole}`;
  const divisor = rest % 4 === 0 ? 2 : rest % 2 === 0 ? 4 : 8;
  return `${whole} ${rest / (8 / divisor)}/${divisor}`;
}
